import React from "react";
import { Mutation } from "react-apollo";
import ReCAPTCHA from "react-google-recaptcha";
import Button from "@streeteasy/design-system/deprecated/Button";
import Checkbox from "@streeteasy/design-system/deprecated/Checkbox";
import Text from "@streeteasy/design-system/deprecated/Text";
import Title from "@streeteasy/design-system/deprecated/Title";
import { bool, func, string } from "prop-types";

import SHARE_ITEM from "../../graphql/mutations/shared";
import VIEWER_QUERY from "../../graphql/queries/viewer";
import {
  BannerShareLink,
  DynamicField,
  DynamicGroup,
  ErrorText,
  Location,
  LocationSpacer,
  ModalContainer,
  ModalErrorBox,
  ModalFooter,
  ModalHeader,
  ModalHeaderMobile,
  ModalInputBox,
  ModalLink,
  ModalMainTitle,
  ModalRecaptcha,
  ModalSubmitBox,
  ModalTextArea,
  ModalTitle
} from "./styled";

class ShareForm extends React.Component {
  state = {
    from: "",
    formErrorMessage: "",
    hasGraphQLError: false,
    isChecked: false,
    isRecaptchaValid: true,
    isValidEmailCC: true,
    isValidEmailFrom: true,
    isValidEmailTo: true,
    message: "",
    recaptchaErrorMessage: "",
    to: ""
  };

  componentWillMount() {
    const { userEmail } = this.props;

    if (userEmail) {
      this.isUserLoggedIn = !!userEmail;
      this.setState({ from: userEmail });
    }
  }

  recaptchaRef = React.createRef();

  formatErrorMessage = field => (
    <ErrorText type="sm">{this.state[field]}</ErrorText>
  );

  handleCheckbox = checked => this.setState({ isChecked: checked });

  handleFieldUpdate = (value, event) => {
    const fieldName = event.target.name;

    if (fieldName === "to" && !this.state.isValidEmailTo) {
      // Remove error styling from to and from fields after user changes fields
      this.setState({ isValidEmailTo: true });
    } else if (fieldName === "from" && !this.state.isValidEmailFrom) {
      this.setState({ isValidEmailFrom: true });
    }

    this.setState({ [fieldName]: value });
  };

  handleMutationError = data => {
    /**
     * Validation errors are returned as an error_details array in the zeroeth element
     * of the graphQLErrors arrays. error_details is an array with the following shape:
     * {message: string, inputType: [array of invalid input name]}
     *
     * Information about non-validation errors (network, server) is accessed through
     * the data field on the error object.
     */

    if (!this.isUserLoggedIn) {
      this.recaptchaRef.current.reset();
    }

    const validationErrors = data?.graphQLErrors?.[0];

    if (validationErrors) {
      return this.processValidationErrors(
        validationErrors.options.error_details
      );
    }

    this.setState({
      formErrorMessage: data.message,
      hasGraphQLError: true
    });

    return false;
  };

  handleRecaptchaChange = () => {
    // Remove any error styling from Recaptcha after user clicks the checkbox
    if (!this.state.isRecaptchaValid) {
      this.setState({ isRecaptchaValid: true });
    }
  };

  handleRecaptchaErrorMessage = () => {
    if (!this.state.isRecaptchaValid) {
      return (
        <ModalErrorBox>
          {this.formatErrorMessage("recaptchaErrorMessage")}
        </ModalErrorBox>
      );
    }

    return false;
  };

  handleRenderErrorMessage = () => {
    if (
      !this.state.isValidEmailFrom ||
      !this.state.isValidEmailTo ||
      this.state.hasGraphQLError
    ) {
      return this.formatErrorMessage("formErrorMessage");
    }

    return false;
  };

  handleSubmit = (e, mutationQuery) => {
    e.preventDefault();

    const recaptchaResponse = this.isUserLoggedIn
      ? ""
      : this.recaptchaRef.current.getValue();

    mutationQuery({
      variables: {
        from: this.state.from,
        to: this.state.to,
        item_id: this.props.itemId,
        item_type: this.props.itemType,
        message: this.state.message,
        copy_to_myself: this.state.isChecked,
        g_recaptcha_response: recaptchaResponse
      },
      refetchQueries: [{ query: VIEWER_QUERY }]
    });

    return false;
  };

  processValidationErrors = arrayOfErrorObjects => {
    /**
     * There are two types of form errors: validation of the to/from fields and recaptcha.
     * There is one slot for to/from validation errors that appears below the form.
     * Recaptcha errors appear beneath the recaptcha checkbox. Because we show only
     * one error for the to/from fields, we prioritize the most descriptive error.
     */

    const invalidFields = [];
    let formErrorMessage = "";
    let recaptchaErrorMessage = "";

    arrayOfErrorObjects.forEach(error => {
      error.input_types.forEach(inputType => {
        if (!invalidFields.includes(inputType)) {
          invalidFields.push(inputType);
        }

        if (inputType === "recaptcha") {
          recaptchaErrorMessage = error.message;
        }

        if (
          (inputType === "to" || inputType === "from") &&
          !/valid email/.test(error.message)
        ) {
          formErrorMessage = error.message;
        }
      });
    });

    if (
      formErrorMessage === "" &&
      (invalidFields.includes("from") ||
        invalidFields.includes("to") ||
        invalidFields.includes("cc"))
    ) {
      formErrorMessage = "Please enter a valid email";
    }

    this.setState({
      formErrorMessage,
      isRecaptchaValid: !invalidFields.includes("recaptcha"),
      isValidEmailCC: !invalidFields.includes("cc"),
      isValidEmailFrom: !invalidFields.includes("from"),
      isValidEmailTo: !invalidFields.includes("to"),
      recaptchaErrorMessage
    });

    return false;
  };

  render() {
    return (
      <ModalContainer>
        <ModalHeaderMobile>
          <ModalTitle type="secondary">
            Share this {this.props.commonType(this.props.itemType)}
          </ModalTitle>
        </ModalHeaderMobile>
        <ModalHeader isAgent={this.props.isAgent}>
          <ModalMainTitle type="primarySm">
            Share this {this.props.commonType(this.props.itemType)}
          </ModalMainTitle>
          <LocationSpacer isAgent={this.props.isAgent}>
            <Location type="secondarySmCaps">{this.props.itemArea}</Location>
          </LocationSpacer>
          <Title type="secondary">
            <b>{this.props.itemAddress}</b>
          </Title>
          {this.props.isAgent && (
            <BannerShareLink>
              You will appear as the only agent in the contact box when you
              share this listing.
            </BannerShareLink>
          )}
        </ModalHeader>
        <Mutation
          mutation={SHARE_ITEM}
          onCompleted={this.props.handleMutationCompleted}
          onError={this.handleMutationError}
        >
          {mutationQuery => (
            <form
              onSubmit={e => {
                this.handleSubmit(e, mutationQuery);
              }}
            >
              <DynamicGroup>
                <DynamicField
                  isInvalid={!this.state.isValidEmailFrom}
                  name="from"
                  onChange={this.handleFieldUpdate}
                  placeholder="Your Email"
                  value={this.state.from}
                />
                <DynamicField
                  isInvalid={
                    !this.state.isValidEmailTo || !this.state.isValidEmailCC
                  }
                  name="to"
                  onChange={this.handleFieldUpdate}
                  placeholder={
                    this.props.isAgent
                      ? "Client Email (use commas for multiple)"
                      : "Your Friend’s Email (separated with commas)"
                  }
                  value={this.state.to}
                />
                <ModalTextArea
                  height="100px"
                  name="message"
                  onChange={this.handleFieldUpdate}
                  placeholder="Message (optional)"
                  value={this.state.message}
                />
              </DynamicGroup>
              <ModalErrorBox>{this.handleRenderErrorMessage()}</ModalErrorBox>
              <ModalInputBox>
                <Checkbox
                  isChecked={this.state.isChecked}
                  label="Send me a copy"
                  onChange={this.handleCheckbox}
                />
              </ModalInputBox>
              {!this.isUserLoggedIn && (
                <ModalRecaptcha>
                  <ReCAPTCHA
                    ref={this.recaptchaRef}
                    onChange={this.handleRecaptchaChange}
                    sitekey={this.props.siteKey}
                  />
                  {this.handleRecaptchaErrorMessage()}
                </ModalRecaptcha>
              )}
              <ModalSubmitBox isPadding={this.isUserLoggedIn}>
                <Button type="submit">Share</Button>
              </ModalSubmitBox>
            </form>
          )}
        </Mutation>
        <ModalFooter>
          <Text type="disclaimer">
            I accept StreetEasy’s
            <ModalLink
              href="https://www.zillow.com/corp/Terms.htm"
              target="_blank"
            >
              Terms of Use
            </ModalLink>
          </Text>
        </ModalFooter>
      </ModalContainer>
    );
  }
}

ShareForm.propTypes = {
  commonType: func.isRequired,
  handleMutationCompleted: func.isRequired,
  isAgent: bool,
  itemAddress: string.isRequired,
  itemArea: string.isRequired,
  itemId: string.isRequired,
  itemType: string.isRequired,
  siteKey: string.isRequired,
  userEmail: string.isRequired
};

ShareForm.defaultProps = {
  isAgent: false
};

export default ShareForm;
